"use client";

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from "react";
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownRehypeSanitizePlugins } from "@/lib/markdown-rehype-plugins";
import {
  XIcon,
  DownloadSimpleIcon,
  FileCloudIcon,
  FunnelIcon,
  ListPlusIcon,
  NewspaperClippingIcon,
  CheckIcon,
  CaretDoubleDownIcon,
  CaretDoubleUpIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/el";
import "dayjs/locale/es";
import { useOptionalI18n } from "@/i18n/LocaleProvider";
import { useTimePilotT } from "@/i18n/useTimePilotT";
import MediaDisplay from "./ui/mediaDisplay";
import { MediaPreviewSlot } from "./ui/media-preview-slot";
import {
  MediaSource,
  MediaType,
  SourceContext,
  FileSummary,
  Source,
} from "@/shared/types";
import { PATH_DISPLAY_WHITESPACE_CLASSNAME } from "@/shared/uiConstants";
import {
  extractPathAfterBulk,
  handleDownload as handleDownloadSource,
  isRestrictedFileType,
  resolveTranscriptUrlFromSourceContext,
  resolvePreferredMediaDisplayForSource,
  isInlineJsonCitationDisplayable,
  isArchiveMemberSearchablePath,
  getDispatchedFilePath,
  pathForAssistantScope,
} from "@/shared/functions";
import { useCellServiceSession } from "@/hooks/useCellServiceSession";
import { isMboxEmailJsonUrl, urlLooksLikeEml } from "@/shared/email-citation";
import { isDisplayableMediaType } from "@/shared/constants";
import { useJsonMediaDisplayEnabled } from '@/hooks/useMediaPlayerTransition';
import type { EventFromChat } from "./answer-item";
import {
  buildSharedDataLocalStorageKey,
  sharedDataCasesMatch,
  useOptionalToolsSharedCaseData,
} from "./tools-shared-case-data";
import { AuthContext } from "@/app/contexts";
import { Button } from "@tranquility-ai/theme-components/button";

dayjs.extend(relativeTime);

const MEDIA_SHARE_STORAGE_KEY_PREFIX = "tpl-media-source-share";
const getStorageKey = (
  caseId: string | undefined,
  tenantFriendlyName: string | null | undefined,
) =>
  buildSharedDataLocalStorageKey(MEDIA_SHARE_STORAGE_KEY_PREFIX, caseId, tenantFriendlyName);
const MEDIA_SHARE_MAX_ENTRIES = 50;
const PREVIEW_MAX_CHARS = 80;

export interface MediaShareEntry {
  id: string;
  mediaType: MediaType;
  mediaUrl?: string;
  mediaSrc?: string;
  /** Canonical citation path for content-only JSON (no mediaUrl). */
  sourceUrl?: string;
  sourceFilePath?: string;
  /** Parent container (mbox/zip) when mediaUrl is an inner extract such as ``.eml``. */
  originalUrl?: string;
  dispatchedFilePath?: string;
  analysisFilePath?: string;
  previewFilePath?: string;
  mediaContext?: SourceContext;
  fileInfo?: FileSummary | null;
  truncatedFilePath?: string;
  content?: string;
  page?: string;
  location?: string;
  timestamp: number;
}

/**
 * Resolved PDF/page index for share entries — no default to 1 (avoids false "page 1" for AV / no-page sources).
 * Aligns with UnifiedSources source identity keys (`pageNumber > 0` only).
 */
export function resolvedShareEntryPage(entry: {
  page?: string;
  mediaContext?: SourceContext;
}): number | undefined {
  const ctx = entry.mediaContext ?? {};
  const fromEntry =
    entry.page != null && String(entry.page).trim() !== ""
      ? Number.parseInt(String(entry.page), 10)
      : NaN;
  if (typeof ctx.pageNumber === "number" && Number.isFinite(ctx.pageNumber) && ctx.pageNumber > 0) {
    return ctx.pageNumber;
  }
  if (typeof ctx.page === "number" && Number.isFinite(ctx.page) && ctx.page > 0) {
    return ctx.page;
  }
  if (Number.isFinite(fromEntry) && fromEntry > 0) {
    return fromEntry;
  }
  return undefined;
}

/** Build a {@link MediaSource} for UnifiedSources / event modal (same shape as search results). */
export function mediaShareEntryToMediaSource(
  entry: MediaShareEntry,
): MediaSource | null {
  const url = (entry.mediaUrl || entry.mediaSrc || "").trim();
  const pageNum = resolvedShareEntryPage(entry);
  const nextCtx: SourceContext = { ...(entry.mediaContext ?? {}) };
  if (pageNum !== undefined) {
    nextCtx.pageNumber = pageNum;
    nextCtx.page = pageNum;
  } else {
    delete nextCtx.pageNumber;
    delete nextCtx.page;
  }
  if (entry.originalUrl) nextCtx.originalUrl = entry.originalUrl;
  // Keep a parent ``.eml`` on context when the entry's dispatched path is the
  // attachment — overwriting it would send ``resolveEmailFetchArgs`` back to ``/mbox``.
  const existingContextDispatched =
    typeof nextCtx.dispatchedFilePath === "string" ? nextCtx.dispatchedFilePath : "";
  if (
    entry.dispatchedFilePath &&
    !(urlLooksLikeEml(existingContextDispatched) && !urlLooksLikeEml(entry.dispatchedFilePath))
  ) {
    nextCtx.dispatchedFilePath = entry.dispatchedFilePath;
  }

  if (!url) {
    // Content-only JSON favorites have no file URL.
    if (entry.mediaType === "json" && entry.content?.trim()) {
      const identityUrl =
        entry.sourceUrl || entry.sourceFilePath || undefined;
      return {
        mediaType: "json",
        sourceType: "json",
        sourceUrl: identityUrl,
        sourceFilePath: entry.sourceFilePath || identityUrl,
        truncatedFilePath: entry.truncatedFilePath,
        sourceContext: nextCtx,
        mediaContext: nextCtx,
        fileInfo: entry.fileInfo ?? undefined,
        content: entry.content,
      };
    }
    return null;
  }

  return {
    sourceType: entry.mediaType,
    sourceUrl: url,
    sourceFilePath: entry.sourceFilePath || url,
    originalUrl: entry.originalUrl,
    dispatchedFilePath: entry.dispatchedFilePath,
    analysisFilePath: entry.analysisFilePath,
    previewFilePath: entry.previewFilePath,
    mediaType: entry.mediaType,
    mediaUrl: url,
    mediaSrc: url,
    truncatedFilePath: entry.truncatedFilePath,
    sourceContext: nextCtx,
    mediaContext: nextCtx,
    fileInfo: entry.fileInfo ?? undefined,
    content: entry.content,
  };
}

function loadMediaShareEntries(storageKey: string): MediaShareEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as MediaShareEntry[];
    return Array.isArray(parsed)
      ? parsed.slice(0, MEDIA_SHARE_MAX_ENTRIES)
      : [];
  } catch {
    return [];
  }
}

export type MediaSourceShareContextValue = {
  addToMediaShare: (
    item: MediaSource | Source | Record<string, unknown>,
    onSuccess?: () => void,
  ) => void;
  toggleDrawer: () => void;
  isDrawerOpen: boolean;
  /** State for Tools Drawer to render MediaSourceShareContent (only set when provider is mounted). */
  entries: MediaShareEntry[];
  setEntries: React.Dispatch<React.SetStateAction<MediaShareEntry[]>>;
  persistEntries: (next: MediaShareEntry[]) => void;
  /** Reload entries from server (shared case) or localStorage. */
  refetchEntries: () => void;
  storageKey: string;
  caseId?: string;
  onAddToTimeline?: (event: EventFromChat) => void;
};

const MediaSourceShareContext =
  createContext<MediaSourceShareContextValue | null>(null);

export function useMediaSourceShare() {
  return useContext(MediaSourceShareContext);
}

function sourcePageFromRecord(src: Record<string, unknown>): number {
  const sourcePage =
    (src.sourceContext as Record<string, unknown>)?.pageNumber ??
    src.pageNumber ??
    src.page ??
    1;
  const pageNum =
    typeof sourcePage === "string"
      ? parseInt(String(sourcePage), 10)
      : Number(sourcePage);
  return Number.isFinite(pageNum) && pageNum > 0 ? pageNum : 1;
}

function sourceOrObjectToMediaSource(
  item: MediaSource | Source | Record<string, unknown>,
  jsonMediaDisplayEnabled = false,
): MediaSource | null {
  const src = item as Record<string, unknown>;
  const asMedia = item as MediaSource;
  const content =
    typeof src.content === "string" ? src.content : asMedia.content;

  const existingUrl = (asMedia.mediaUrl || asMedia.mediaSrc || "").trim();
  if (
    asMedia.mediaType &&
    existingUrl &&
    !isArchiveMemberSearchablePath(existingUrl) &&
    isDisplayableMediaType(asMedia.mediaType, {
      jsonMediaDisplay: jsonMediaDisplayEnabled,
    })
  ) {
    return asMedia;
  }

  // Content-only JSON MediaSource (no media URL).
  if (
    asMedia.mediaType === "json" &&
    jsonMediaDisplayEnabled &&
    typeof content === "string" &&
    content.trim() &&
    !existingUrl
  ) {
    return {
      ...asMedia,
      mediaType: "json",
      content,
    };
  }

  const preferred = resolvePreferredMediaDisplayForSource(
    item as Source,
    (src.fileInfo as FileSummary | null | undefined) ?? null,
  );

  if (
    preferred.mediaUrl &&
    !isArchiveMemberSearchablePath(preferred.mediaUrl) &&
    isDisplayableMediaType(preferred.mediaType, {
      jsonMediaDisplay: jsonMediaDisplayEnabled,
    })
  ) {
    const pageNum = sourcePageFromRecord(src);
    return {
      mediaType: preferred.mediaType,
      mediaUrl: preferred.mediaUrl,
      mediaSrc: preferred.mediaUrl,
      content: typeof content === "string" ? content : undefined,
      mediaContext: {
        ...(src.sourceContext as SourceContext | undefined),
        pageNumber: pageNum,
        page: pageNum,
        previewUrl: src.previewFilePath as string | undefined,
        transcriptUrl: resolveTranscriptUrlFromSourceContext(
          src.sourceContext as Record<string, unknown> | undefined,
        ),
        startTime: (src.sourceContext as Record<string, unknown>)?.startTime as
          | number
          | undefined,
      },
      fileInfo: src.fileInfo as FileSummary | null | undefined,
      previewFilePath: src.previewFilePath as string | undefined,
      sourceFilePath: src.sourceFilePath as string | undefined,
      originalUrl:
        (src.originalUrl as string | undefined) ||
        ((src.sourceContext as SourceContext | undefined)?.originalUrl as string | undefined),
      dispatchedFilePath:
        (src.dispatchedFilePath as string | undefined) ||
        getDispatchedFilePath(item as Source) ||
        undefined,
      analysisFilePath: src.analysisFilePath as string | undefined,
      sourceUrl:
        (src.sourceUrl as string | undefined) ||
        (src.sourceFilePath as string | undefined),
    };
  }

  if (
    isInlineJsonCitationDisplayable(
      item as Source,
      preferred.mediaType,
      jsonMediaDisplayEnabled,
    )
  ) {
    const pageNum = sourcePageFromRecord(src);
    return {
      mediaType: "json",
      content: typeof content === "string" ? content : undefined,
      mediaContext: {
        ...(src.sourceContext as SourceContext | undefined),
        pageNumber: pageNum,
        page: pageNum,
        transcriptUrl: resolveTranscriptUrlFromSourceContext(
          src.sourceContext as Record<string, unknown> | undefined,
        ),
        startTime: (src.sourceContext as Record<string, unknown>)?.startTime as
          | number
          | undefined,
      },
      fileInfo: src.fileInfo as FileSummary | null | undefined,
      previewFilePath: src.previewFilePath as string | undefined,
      sourceFilePath: src.sourceFilePath as string | undefined,
      sourceUrl:
        (src.sourceUrl as string | undefined) ||
        (src.sourceFilePath as string | undefined),
    };
  }

  return null;
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/** Keep a stored parent ``.eml`` when a later add only carries the attachment. */
function mergeFavoriteMediaContext(
  existing: SourceContext | undefined,
  incoming: SourceContext | undefined,
): SourceContext | undefined {
  if (!existing && !incoming) return existing;
  const defined = (ctx: SourceContext | undefined): SourceContext => {
    if (!ctx) return {};
    return Object.fromEntries(
      Object.entries(ctx).filter(([, value]) => value !== undefined && value !== null && value !== ""),
    ) as SourceContext;
  };
  const existingDefined = defined(existing);
  const incomingDefined = defined(incoming);
  const next: SourceContext = { ...existingDefined, ...incomingDefined };
  const existingDispatched =
    typeof existingDefined.dispatchedFilePath === "string"
      ? existingDefined.dispatchedFilePath
      : undefined;
  const incomingDispatched =
    typeof incomingDefined.dispatchedFilePath === "string"
      ? incomingDefined.dispatchedFilePath
      : undefined;
  if (
    urlLooksLikeEml(existingDispatched) &&
    incomingDispatched &&
    !urlLooksLikeEml(incomingDispatched)
  ) {
    next.dispatchedFilePath = existingDispatched;
  }
  return Object.keys(next).length ? next : existing;
}

function emailContainerFieldsFromShareItem(
  item: MediaSource | Source | Record<string, unknown>,
  mediaObj: MediaSource,
): Pick<
  MediaShareEntry,
  "originalUrl" | "dispatchedFilePath" | "analysisFilePath" | "previewFilePath"
> {
  const raw = item as Record<string, unknown>;
  const asMedia = item as MediaSource;
  const asSource = item as Source;
  const ctx = (asSource.sourceContext ?? asMedia.mediaContext ?? {}) as Record<string, unknown>;
  return {
    originalUrl:
      stringOrUndefined(asMedia.originalUrl) ||
      stringOrUndefined(asSource.originalUrl) ||
      stringOrUndefined(raw.originalUrl) ||
      stringOrUndefined(ctx.originalUrl) ||
      stringOrUndefined(mediaObj.originalUrl),
    dispatchedFilePath:
      stringOrUndefined(asMedia.dispatchedFilePath) ||
      getDispatchedFilePath(asSource) ||
      stringOrUndefined(mediaObj.dispatchedFilePath) ||
      undefined,
    analysisFilePath:
      stringOrUndefined(asSource.analysisFilePath) ||
      stringOrUndefined(asMedia.analysisFilePath) ||
      stringOrUndefined(raw.analysisFilePath),
    previewFilePath:
      stringOrUndefined(asSource.previewFilePath) ||
      stringOrUndefined(asMedia.previewFilePath) ||
      stringOrUndefined(mediaObj.previewFilePath),
  };
}

/** Optional drawer API injected by Tools Drawer when mounted (avoids circular dependency). */
export type InjectedDrawerApi = {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  openDrawerToTab: (
    tab: "sources" | "recently-copied" | "research-notes" | "captures",
  ) => void;
};

export function MediaSourceShareProvider({
  children,
  caseId,
  onAddToTimeline,
  injectDrawer,
}: {
  children: React.ReactNode;
  caseId?: string;
  onAddToTimeline?: (event: EventFromChat) => void;
  injectDrawer?: InjectedDrawerApi | null;
}) {
  const auth = useContext(AuthContext);
  const jsonMediaDisplayEnabled = useJsonMediaDisplayEnabled();
  const remote = useOptionalToolsSharedCaseData();
  const tenantFriendlyName = remote?.tenantFriendlyName ?? auth.tenantName ?? null;
  const storageKey = getStorageKey(caseId, tenantFriendlyName);
  const useRemoteShare = Boolean(
    caseId &&
      remote &&
      sharedDataCasesMatch(caseId, remote.caseId, remote.tenantFriendlyName),
  );

  const [localEntries, setLocalEntries] = useState<MediaShareEntry[]>(() =>
    useRemoteShare ? [] : loadMediaShareEntries(storageKey),
  );
  const [localOpen, setLocalOpen] = useState(false);

  useEffect(() => {
    if (!useRemoteShare) {
      setLocalEntries(loadMediaShareEntries(storageKey));
    }
  }, [storageKey, useRemoteShare]);

  const entries = useRemoteShare
    ? remote!.hydrated
      ? remote!.sources
      : []
    : localEntries;
  const setEntries = useRemoteShare ? remote!.setSources : setLocalEntries;

  const persistEntries = useCallback(
    (next: MediaShareEntry[]) => {
      if (useRemoteShare) {
        return;
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [useRemoteShare, storageKey],
  );

  const remoteRefetch = remote?.refetch;
  const refetchEntries = useCallback(() => {
    if (useRemoteShare) {
      if (remoteRefetch) void remoteRefetch();
      return;
    }
    setLocalEntries(loadMediaShareEntries(storageKey));
  }, [useRemoteShare, remoteRefetch, storageKey]);

  const isDrawerOpen = injectDrawer ? injectDrawer.isOpen : localOpen;
  const toggleDrawer = useCallback(() => {
    if (injectDrawer) {
      if (injectDrawer.isOpen) {
        injectDrawer.closeDrawer();
      } else {
        injectDrawer.openDrawerToTab("sources");
      }
    } else {
      setLocalOpen((prev) => !prev);
    }
  }, [injectDrawer]);

  const addToMediaShare = useCallback(
    (
      item: MediaSource | Source | Record<string, unknown>,
      onSuccess?: () => void,
    ) => {
      const asMedia = item as MediaSource;
      const existingUrl = (asMedia.mediaUrl || asMedia.mediaSrc || "").trim();
      const alreadyResolved =
        !!asMedia.mediaType &&
        ((existingUrl &&
          !isArchiveMemberSearchablePath(existingUrl) &&
          isDisplayableMediaType(asMedia.mediaType, {
            jsonMediaDisplay: jsonMediaDisplayEnabled,
          })) ||
          (asMedia.mediaType === "json" &&
            jsonMediaDisplayEnabled &&
            !!asMedia.content?.trim() &&
            (!existingUrl || isArchiveMemberSearchablePath(existingUrl))));
      const mediaObj = alreadyResolved
        ? {
            ...asMedia,
            ...(asMedia.mediaType === "json" &&
            jsonMediaDisplayEnabled &&
            !!asMedia.content?.trim() &&
            (!existingUrl || isArchiveMemberSearchablePath(existingUrl))
              ? { mediaUrl: undefined, mediaSrc: undefined }
              : {}),
          }
        : sourceOrObjectToMediaSource(item, jsonMediaDisplayEnabled);
      if (
        !mediaObj ||
        !isDisplayableMediaType(mediaObj.mediaType, {
          jsonMediaDisplay: jsonMediaDisplayEnabled,
        })
      ) {
        return;
      }

      const url = (mediaObj.mediaUrl || mediaObj.mediaSrc || "").trim();
      const raw = item as Record<string, unknown>;
      const content =
        (raw.content as string | undefined) ?? mediaObj.content ?? undefined;
      if (!url && !(mediaObj.mediaType === "json" && content?.trim())) {
        return;
      }

      const pathForLabel =
        ((item as Record<string, unknown>).truncatedFilePath as string) ||
        url ||
        (content?.trim() ? "JSON citation" : "Unknown");
      const truncated = url
        ? extractPathAfterBulk(pathForLabel, true).truncatedFilePath
        : pathForLabel;

      const page = (raw.page as string) ?? undefined;
      const location = (raw.location as string) ?? undefined;
      const emailContainerFields = emailContainerFieldsFromShareItem(item, mediaObj);

      setEntries((prev) => {
        const now = Date.now();
        const contentNorm = (content ?? "").trim();
        const newPage = resolvedShareEntryPage({
          page,
          mediaContext: mediaObj.mediaContext,
        });
        const newSourcePath = (
          mediaObj.sourceUrl ||
          mediaObj.sourceFilePath ||
          ((item as Record<string, unknown>).sourceUrl as string | undefined) ||
          ((item as Record<string, unknown>).sourceFilePath as string | undefined) ||
          ""
        ).trim();
        const existing = prev.find((e) => {
          const eUrl = (e.mediaUrl || e.mediaSrc || "").trim();
          if (url) {
            return (
              (e.mediaUrl === url || e.mediaSrc === url) &&
              resolvedShareEntryPage(e) === newPage &&
              (e.content ?? "").trim() === contentNorm
            );
          }
          // Content-only JSON: require same excerpt, page, and canonical source
          // path so identical JSON from different files stay distinct favorites.
          const eSourcePath = (e.sourceUrl || e.sourceFilePath || "").trim();
          if (newSourcePath && eSourcePath && newSourcePath !== eSourcePath) {
            return false;
          }
          return (
            !eUrl &&
            e.mediaType === "json" &&
            (e.content ?? "").trim() === contentNorm &&
            resolvedShareEntryPage(e) === newPage
          );
        });
        let next: MediaShareEntry[];
        if (existing) {
          next = [
            {
              ...existing,
              content: content ?? existing.content,
              page: page ?? existing.page,
              location: location ?? existing.location,
              sourceUrl:
                mediaObj.sourceUrl ||
                mediaObj.sourceFilePath ||
                existing.sourceUrl ||
                ((item as Record<string, unknown>).sourceUrl as string | undefined) ||
                existing.sourceFilePath,
              sourceFilePath:
                mediaObj.sourceFilePath ||
                mediaObj.sourceUrl ||
                existing.sourceFilePath ||
                ((item as Record<string, unknown>).sourceFilePath as string | undefined) ||
                existing.sourceUrl,
              ...Object.fromEntries(
                Object.entries(emailContainerFields).filter(([, value]) => value !== undefined),
              ),
              mediaContext: mergeFavoriteMediaContext(
                existing.mediaContext,
                mediaObj.mediaContext,
              ),
              timestamp: now,
            },
            ...prev.filter((e) => e.id !== existing.id),
          ];
        } else {
          const created: MediaShareEntry = {
              id: crypto.randomUUID(),
              mediaType: mediaObj.mediaType ?? "unknown",
              mediaUrl: mediaObj.mediaUrl,
              mediaSrc: mediaObj.mediaSrc,
              sourceUrl:
                mediaObj.sourceUrl ||
                mediaObj.sourceFilePath ||
                ((item as Record<string, unknown>).sourceUrl as string | undefined) ||
                ((item as Record<string, unknown>).sourceFilePath as string | undefined),
              sourceFilePath:
                mediaObj.sourceFilePath ||
                mediaObj.sourceUrl ||
                ((item as Record<string, unknown>).sourceFilePath as string | undefined) ||
                ((item as Record<string, unknown>).sourceUrl as string | undefined),
              ...emailContainerFields,
              mediaContext: mediaObj.mediaContext,
              fileInfo: mediaObj.fileInfo,
              truncatedFilePath: truncated,
              content,
              page,
              location,
              timestamp: now,
          };
          next = [created, ...prev].slice(0, MEDIA_SHARE_MAX_ENTRIES);
        }
        persistEntries(next);
        return next;
      });
      onSuccess?.();
    },
    [persistEntries, setEntries, jsonMediaDisplayEnabled],
  );

  const contextValue = useMemo(
    (): MediaSourceShareContextValue => ({
      addToMediaShare,
      toggleDrawer,
      isDrawerOpen,
      entries,
      setEntries,
      persistEntries,
      refetchEntries,
      storageKey,
      caseId,
      onAddToTimeline,
    }),
    [
      addToMediaShare,
      toggleDrawer,
      isDrawerOpen,
      entries,
      setEntries,
      persistEntries,
      refetchEntries,
      storageKey,
      caseId,
      onAddToTimeline,
    ],
  );

  return (
    <MediaSourceShareContext.Provider value={contextValue}>
      {children}
    </MediaSourceShareContext.Provider>
  );
}

export function MediaSourceShareContent({
  entries,
  setEntries,
  persistEntries,
  storageKey,
  caseId,
  onAddToTimeline,
  addSharedSourceToEvent,
  eventSharedSourceAddReady = false,
}: {
  entries: MediaShareEntry[];
  setEntries: React.Dispatch<React.SetStateAction<MediaShareEntry[]>>;
  persistEntries: (next: MediaShareEntry[]) => void;
  storageKey: string;
  caseId?: string;
  onAddToTimeline?: (event: EventFromChat) => void;
  /** From tools drawer: append a source to the open event modal's selected-sources list. */
  addSharedSourceToEvent?: (source: MediaSource) => boolean;
  eventSharedSourceAddReady?: boolean;
}) {
  const t = useTimePilotT();
  const locale = useOptionalI18n()?.locale ?? "en";
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [justAddedToEventSources, setJustAddedToEventSources] = useState(false);
  const [addedToScopeId, setAddedToScopeId] = useState<string | null>(null);
  const [copiedPathId, setCopiedPathId] = useState<string | null>(null);
  const [createdEventId, setCreatedEventId] = useState<string | null>(null);
  const [isCitationExpanded, setIsCitationExpanded] = useState(true);

  const entryToSource = useCallback(
    (entry: MediaShareEntry): Source => ({
      sourceFilePath:
        entry.sourceFilePath ||
        entry.sourceUrl ||
        entry.mediaUrl ||
        entry.mediaSrc,
      sourceUrl:
        entry.sourceUrl ||
        entry.sourceFilePath ||
        entry.mediaUrl ||
        entry.mediaSrc,
      truncatedFilePath: entry.truncatedFilePath,
      sourceContext: entry.mediaContext,
      fileInfo: entry.fileInfo,
      page: entry.page ?? entry.mediaContext?.pageNumber?.toString(),
      content: entry.content,
      location: entry.location,
    }),
    [],
  );

  const { cellId, accessToken: cellAccessToken } = useCellServiceSession();

  const handleDownload = useCallback(
    (entry: MediaShareEntry) => {
      const session = cellId && cellAccessToken ? { cellId, accessToken: cellAccessToken } : undefined;
      handleDownloadSource(entryToSource(entry), session);
    },
    [entryToSource, cellId, cellAccessToken],
  );

  const handleAddToScope = useCallback(
    (entry: MediaShareEntry) => {
      const path = pathForAssistantScope(entryToSource(entry));
      if (!path || !caseId) return;
      const key = `${caseId}-scopesObject`;
      const current = JSON.parse(
        sessionStorage.getItem(key) ||
          '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }',
      );
      current.files = Array.from(new Set([...current.files, path]));
      sessionStorage.setItem(key, JSON.stringify(current));
      window.dispatchEvent(
        new CustomEvent("scopes-updated", { detail: { caseId } }),
      );
      setAddedToScopeId(entry.id);
      setTimeout(() => setAddedToScopeId(null), 1500);
    },
    [caseId],
  );

  const handleAddSelectedEntryToEventSources = useCallback(() => {
    if (!addSharedSourceToEvent || !eventSharedSourceAddReady) return;
    const entry =
      entries.find((e) => e.id === selectedId) ?? (entries.length > 0 ? entries[0] : undefined);
    if (!entry) return;
    const source = mediaShareEntryToMediaSource(entry);
    if (!source) return;
    const ok = addSharedSourceToEvent(source);
    if (!ok) return;
    setJustAddedToEventSources(true);
    setTimeout(() => setJustAddedToEventSources(false), 1500);
  }, [
    addSharedSourceToEvent,
    eventSharedSourceAddReady,
    entries,
    selectedId,
  ]);

  const handleCopyPath = useCallback(async (entry: MediaShareEntry) => {
    const path =
      entry.truncatedFilePath || entry.mediaUrl || entry.mediaSrc || "";
    if (!path) return;
    try {
      await navigator.clipboard.writeText(path);
      setCopiedPathId(entry.id);
      setTimeout(() => setCopiedPathId(null), 1500);
    } catch {
      /* ignore */
    }
  }, []);

  const handleCreateEvent = useCallback(
    (entry: MediaShareEntry) => {
      if (!onAddToTimeline || !caseId) return;
      const source = entryToSource(entry);
      const mediaObj = entry as MediaSource;
      const event: EventFromChat = {
        answerText: "FROM_SOURCE",
        sources: [source],
        media: [mediaObj],
        autogen: false,
        epochTimeSecs: Math.floor(Date.now() / 1000),
      };
      onAddToTimeline(event);
      setCreatedEventId(entry.id);
      setTimeout(() => setCreatedEventId(null), 1500);
    },
    [onAddToTimeline, caseId, entryToSource],
  );

  useEffect(() => {
    setSelectedId(null);
  }, [storageKey]);

  useEffect(() => {
    setIsCitationExpanded(true);
  }, [selectedId]);

  const remoteShare = useOptionalToolsSharedCaseData();
  const skipStorageSync = Boolean(
    caseId && remoteShare && remoteShare.caseId === caseId,
  );

  useEffect(() => {
    if (skipStorageSync) return;
    const handler = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue != null) {
        try {
          const parsed = JSON.parse(event.newValue) as MediaShareEntry[];
          if (Array.isArray(parsed)) {
            setEntries(parsed.slice(0, MEDIA_SHARE_MAX_ENTRIES));
          }
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey, setEntries, skipStorageSync]);

  const handleRemove = useCallback(
    (entryId: string) => {
      setEntries((prev) => {
        const next = prev.filter((e) => e.id !== entryId);
        persistEntries(next);
        return next;
      });
      setSelectedId((prev) => (prev === entryId ? null : prev));
    },
    [persistEntries, setEntries],
  );

  const selectedEntry = entries.find((e) => e.id === selectedId) ?? entries[0];
  const selectedEntryMediaItem = useMemo((): MediaSource | null => {
    if (!selectedEntry) return null;
    const mediaUrl = (selectedEntry.mediaUrl || selectedEntry.mediaSrc || "").trim();
    const mediaContext = {
      ...selectedEntry.mediaContext,
      htmlChunk: selectedEntry.content ? selectedEntry.content.match(/page \d+/i)?.[0] || "" : "",
      pageNumber: selectedEntry.page
        ? parseInt(selectedEntry.page, 10)
        : selectedEntry.mediaContext?.pageNumber ?? 1,
    };
    const base = { ...selectedEntry, mediaContext } as MediaSource;
    if (!mediaUrl) return base;

    const attachmentUrl = (
      (typeof selectedEntry.mediaContext?.attachmentUrl === "string" &&
        selectedEntry.mediaContext.attachmentUrl) ||
      selectedEntry.analysisFilePath ||
      ""
    ).trim();
    const isEmailAttachment =
      !!attachmentUrl &&
      !urlLooksLikeEml(attachmentUrl) &&
      !isMboxEmailJsonUrl(attachmentUrl) &&
      (mediaUrl === attachmentUrl ||
        selectedEntry.sourceFilePath === attachmentUrl ||
        selectedEntry.dispatchedFilePath === attachmentUrl);
    if (!isEmailAttachment) return base;

    return {
      ...base,
      // Prevent MediaDisplay from treating this as mailbox-backed media.
      // For attachment favorites we want direct PDF/image/video fetches.
      originalUrl: mediaUrl,
      sourceFilePath: mediaUrl,
      sourceUrl: mediaUrl,
      dispatchedFilePath: mediaUrl,
      previewFilePath: undefined,
      fileInfo: undefined,
      mediaContext: {
        ...mediaContext,
        dispatchedFilePath: mediaUrl,
        attachmentUrl: mediaUrl,
      },
    } as MediaSource;
  }, [selectedEntry]);

  const labelForEntry = (e: MediaShareEntry) => {
    const p = e.truncatedFilePath;
    if (p && p.trim()) return p;
    const u = e.mediaUrl || e.mediaSrc || "";
    if (u) {
      const parts = u.split("/");
      return parts[parts.length - 1] || u;
    }
    if (e.mediaType === "json" && e.content?.trim()) {
      return t("sources.jsonCitation");
    }
    return t("sources.unknown");
  };

  const previewLabel = (label: string) => {
    const t = label.trim();
    if (t.length <= PREVIEW_MAX_CHARS) return t;
    return t.slice(0, PREVIEW_MAX_CHARS) + "…";
  };

  return (
    <div className="flex flex-1 min-h-0 flex-row overflow-hidden">
        <div
          className={`flex ${entries.length > 0 ? "w-[280px]" : "w-full"} shrink-0 flex-col ${entries.length > 0 ? "border-r border-white/20" : ""} overflow-hidden`}
        >
            <div className="shrink-0 px-3 py-2 border-b border-white/20">
              <span className="text-white/80 font-light text-[13px] tracking-wider">
                {t("sources.addedItems")}
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {entries.length === 0 ? (
                <p className="px-3 py-4 text-white/50 text-xs">
                  {t("sources.favoritesHint")}
                </p>
              ) : (
                <ul className="flex flex-col gap-0 list-none pl-0">
                  {entries.map((entry) => (
                    <li
                      key={entry.id}
                      className={`flex items-start gap-2 border-b border-white/10 px-3 py-2 cursor-pointer ${
                        selectedId === entry.id
                          ? "bg-white/10"
                          : "hover:bg-white/5"
                      }`}
                      onClick={() => setSelectedId(entry.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-white/90 text-xs line-clamp-2 break-words ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}
                          title={labelForEntry(entry)}
                        >
                          {previewLabel(labelForEntry(entry))}
                        </p>
                        <p className="text-white/50 text-[11px] mt-1">
                          {dayjs(entry.timestamp).locale(locale).fromNow()}
                        </p>
                      </div>
                      <Button
                        type="button"
                        iconOnly
                        size="sm"
                        style="text"
                        color="light"
                        background="inverse"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove(entry.id);
                        }}
                        className="rounded p-1 shrink-0 cursor-pointer text-white/60 hover:bg-white/10 hover:text-white"
                        aria-label={t("sources.removeFromList")}
                      >
                        <XIcon weight="bold" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
        </div>
          {entries.length > 0 && <div className="flex flex-1 min-w-0 min-h-0 flex-col p-3 overflow-hidden">
            {selectedEntry ? (
              <>
                <div className="flex flex-row gap-1 shrink-0 pb-2 border-b border-white/20 mb-2">
                  <TpTooltip content={t("sources.download")} placement="bottom" delayDuration={0}>
                    <Button
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      onClick={() => handleDownload(selectedEntry)}
                      disabled={isRestrictedFileType(selectedEntry)}
                      aria-label={t("sources.download")}
                    >
                      <DownloadSimpleIcon />
                    </Button>
                  </TpTooltip>
                  <TpTooltip content={t("sources.addToScope")} placement="bottom" delayDuration={0}>
                    <Button
                      iconOnly
                      size="sm"
                      style={addedToScopeId === selectedEntry.id ? "solid" : "text"}
                      color={addedToScopeId === selectedEntry.id ? "accent" : "light"}
                      background="inverse"
                      onClick={() => handleAddToScope(selectedEntry)}
                      disabled={!caseId}
                      aria-label={t("sources.addToScope")}
                    >
                      {addedToScopeId === selectedEntry.id ? (
                        <CheckIcon />
                      ) : (
                        <FunnelIcon />
                      )}
                    </Button>
                  </TpTooltip>
                  <TpTooltip
                    content={
                      copiedPathId === selectedEntry.id
                      ? t("sources.copied")
                      : t("sources.copyPath")
                    }
                    placement="bottom"
                    delayDuration={0}
                  >
                    <Button
                      iconOnly
                      size="sm"
                      style={copiedPathId === selectedEntry.id ? "solid" : "text"}
                      color={copiedPathId === selectedEntry.id ? "accent" : "light"}
                      background="inverse"
                      onClick={() => handleCopyPath(selectedEntry)}
                      aria-label={
                        copiedPathId === selectedEntry.id
                          ? t("sources.copied")
                          : t("sources.copyPath")
                      }
                    >
                      {copiedPathId === selectedEntry.id ? (
                        <CheckIcon />
                      ) : (
                        <FileCloudIcon />
                      )}
                    </Button>
                  </TpTooltip>
                  <TpTooltip
                    content={
                      createdEventId === selectedEntry.id
                      ? t("sources.eventCreated")
                      : t("sources.createEvent")
                    }
                    placement="bottom"
                    delayDuration={0}
                  >
                    <Button
                      iconOnly
                      size="sm"
                      style={createdEventId === selectedEntry.id ? "solid" : "text"}
                      color={createdEventId === selectedEntry.id ? "accent" : "light"}
                      background="inverse"
                      onClick={() => handleCreateEvent(selectedEntry)}
                      disabled={!onAddToTimeline || !caseId}
                      aria-label={
                        createdEventId === selectedEntry.id
                          ? t("sources.eventCreated")
                          : t("sources.createEvent")
                      }
                    >
                      {createdEventId === selectedEntry.id ? (
                        <CheckIcon />
                      ) : (
                        <ListPlusIcon />
                      )}
                    </Button>
                  </TpTooltip>
                  <TpTooltip
                    content={
                      eventSharedSourceAddReady
                      ? t("sources.addToOpenEvent")
                      : t("sources.openEventFirst")
                    }
                    placement="bottom"
                    delayDuration={0}
                  >
                    <Button
                      type="button"
                      iconOnly
                      size="sm"
                      style={justAddedToEventSources ? "solid" : "text"}
                      color={justAddedToEventSources ? "accent" : "light"}
                      background="inverse"
                      onClick={handleAddSelectedEntryToEventSources}
                      disabled={!eventSharedSourceAddReady}
                      aria-label={t("sources.addToEventSources")}
                    >
                      {justAddedToEventSources ? (
                        <CheckIcon />
                      ) : (
                        <NewspaperClippingIcon />
                      )}
                    </Button>
                  </TpTooltip>
                </div>
                {((selectedEntry.page && !isNaN(parseInt(selectedEntry.page))) ||
                  ((selectedEntry.content && selectedEntry.content.trim() !== "") &&
                    (!selectedEntry.location?.includes("/bkb/slow/transcribe/") &&
                      !selectedEntry.location?.endsWith(".transcript.txt")))) && (
                  <div className="flex flex-row items-top justify-start w-full shrink-0 pb-2 border-b border-white/20 mb-2">
                    {((selectedEntry.content && selectedEntry.content.trim() !== "") &&
                      (!selectedEntry.location?.includes("/bkb/slow/transcribe/") &&
                        !selectedEntry.location?.endsWith(".transcript.txt"))) && (
                      <div
                        className="cursor-pointer w-[20px] h-[20px] pl-1 pr-2"
                        onClick={() => setIsCitationExpanded((p) => !p)}
                      >
                        {isCitationExpanded ? (
                          <CaretDoubleUpIcon size={20} />
                        ) : (
                          <CaretDoubleDownIcon size={20} />
                        )}
                      </div>
                    )}
                    <div
                      className={`box-border flex flex-col gap-0 flex-1 min-w-0 ${
                        (selectedEntry.content && selectedEntry.content.trim() !== "" &&
                          !selectedEntry.location?.includes("/bkb/slow/transcribe/") &&
                          !selectedEntry.location?.endsWith(".transcript.txt")) && !isCitationExpanded
                          ? "h-[21px] max-h-[21px] overflow-hidden opacity-0 transition-opacity duration-300 ease-in-out"
                          : ""
                      }`}
                    >
                      {selectedEntry.page && !isNaN(parseInt(selectedEntry.page)) && (
                        <div className="text-white font-normal text-base mb-2">
                          Page {selectedEntry.page}
                        </div>
                      )}
                      {((selectedEntry.content && selectedEntry.content.trim() !== "") &&
                        (!selectedEntry.location?.includes("/bkb/slow/transcribe/") &&
                          !selectedEntry.location?.endsWith(".transcript.txt"))) && (
                        <div className="flex flex-col gap-2">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={markdownRehypeSanitizePlugins}
                            components={{
                              p: ({ node, children }) => <span className="mb-2 leading-normal">{children}</span>,
                              h2: ({ node, children }) => <span className="mb-2 leading-normal">{children}</span>,
                              li: ({ node, children }) => <li className="mb-2 leading-normal">{children}</li>,
                              a: ({ node, children }) => <a className="text-brand-blue-light italic">{children}</a>,
                            }}
                          >
                            {selectedEntry.content?.replace("```json ", "")}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <MediaPreviewSlot fillContainer mediaItem={selectedEntryMediaItem ?? selectedEntry}>
                  <MediaDisplay
                    mediaItem={
                      selectedEntryMediaItem ??
                      ({
                        ...selectedEntry,
                        mediaContext: {
                          ...selectedEntry.mediaContext,
                          htmlChunk: selectedEntry.content ? selectedEntry.content.match(/page \d+/i)?.[0] || "" : "",
                          pageNumber: selectedEntry.page ? parseInt(selectedEntry.page, 10) : selectedEntry.mediaContext?.pageNumber ?? 1,
                        },
                      } as MediaSource)
                    }
                    sourcesExpanded={false}
                    fillContainer
                  />
                </MediaPreviewSlot>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center text-white/50 text-sm">
                Select an item to view
              </div>
            )}
          </div>}
    </div>
  );
}

export default MediaSourceShareProvider;
